import { useEffect, useMemo, useState } from "react";
import { FiAlertCircle, FiX } from "react-icons/fi";
import { paymentApi } from "../../services/paymentApi";

const paymentMethods = [
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "cash", label: "Cash" },
  { value: "card", label: "Card" },
  { value: "online", label: "Online" },
  { value: "other", label: "Other" },
];

const paymentStatuses = [
  { value: "pending", label: "Pending" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "refunded", label: "Refunded" },
];

const paymentTypes = [
  { value: "freight", label: "Freight" },
  { value: "customs", label: "Customs" },
  { value: "port_charges", label: "Port Charges" },
  { value: "uk_delivery", label: "UK Delivery" },
  { value: "insurance", label: "Insurance" },
  { value: "forwarder_fee", label: "Forwarder Fee" },
  { value: "invoice_payment", label: "Invoice Payment" },
  { value: "other", label: "Other" },
];

const currencyOptions = ["GBP", "USD", "EUR"];

const money = (value) => Number(Number(value || 0).toFixed(2));

const initialForm = {
  invoiceId: "",
  vendorId: "",
  paidTo: "",
  paymentType: "freight",
  amount: "",
  currency: "GBP",
  paymentMethod: "bank_transfer",
  status: "completed",
  paymentDate: new Date().toISOString().slice(0, 10),
  dueDate: "",
  notes: "",
};

export default function PaymentFormModal({
  open,
  onClose,
  onSaved,
  payment,
  invoices = [],
  vendors = [],
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    if (!open) return;

    setError("");

    if (payment) {
      setForm({
        invoiceId: payment.invoiceId ? String(payment.invoiceId) : "",
        vendorId: payment.vendorId ? String(payment.vendorId) : "",
        paidTo: payment.paidTo || "",
        paymentType: payment.paymentType || "freight",
        amount: payment.amount !== undefined ? String(payment.amount) : "",
        currency: payment.currency || "GBP",
        paymentMethod: payment.paymentMethod || "bank_transfer",
        status: payment.status || "completed",
        paymentDate:
          payment.paymentDate || new Date().toISOString().slice(0, 10),
        dueDate: payment.dueDate || "",
        notes: payment.notes || "",
      });
    } else {
      setForm({
        ...initialForm,
        paymentDate: new Date().toISOString().slice(0, 10),
      });
    }
  }, [open, payment]);

  const selectedInvoice = useMemo(
    () => invoices.find((inv) => String(inv.id) === String(form.invoiceId)),
    [invoices, form.invoiceId]
  );

  const selectedVendor = useMemo(
    () => vendors.find((v) => String(v.id) === String(form.vendorId)),
    [vendors, form.vendorId]
  );

  const invoiceBalance = money(selectedInvoice?.balanceAmount);
  const invoiceTotal = money(selectedInvoice?.totalAmount);
  const invoicePaid = money(selectedInvoice?.paidAmount);

  const editingSameCompletedInvoice =
    Boolean(payment) &&
    payment.status === "completed" &&
    String(payment.invoiceId || "") === String(form.invoiceId || "");

  const availableInvoiceBalance = money(
    invoiceBalance +
      (editingSameCompletedInvoice ? Number(payment.amount || 0) : 0)
  );

  const displayCurrency =
    selectedInvoice?.currency || form.currency || "GBP";

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleInvoiceChange = (e) => {
    const id = e.target.value;
    const invoice = invoices.find(
      (item) => String(item.id) === String(id)
    );

    const isOriginalCompletedInvoice =
      Boolean(payment) &&
      payment.status === "completed" &&
      String(payment.invoiceId || "") === String(id || "");

    const availableBalance = invoice
      ? money(
          Number(invoice.balanceAmount || 0) +
            (isOriginalCompletedInvoice
              ? Number(payment.amount || 0)
              : 0)
        )
      : null;

    setForm((prev) => ({
      ...prev,
      invoiceId: id,
      vendorId: invoice?.vendorId
        ? String(invoice.vendorId)
        : prev.vendorId,
      paidTo:
        invoice?.supplierName ||
        invoice?.vendor?.name ||
        prev.paidTo,
      amount:
        availableBalance !== null
          ? String(availableBalance)
          : prev.amount,
      currency:
        invoice?.currency || prev.currency || "GBP",
      paymentType: invoice
        ? "invoice_payment"
        : prev.paymentType,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      const amount = Number(form.amount || 0);

      if (!amount || amount <= 0) {
        throw new Error("Amount must be greater than zero");
      }

      if (!form.invoiceId && !form.vendorId && !form.paidTo.trim()) {
        throw new Error("Please select a forwarder or enter paid to name");
      }

      if (
        selectedInvoice &&
        form.status === "completed" &&
        amount > availableInvoiceBalance
      ) {
        throw new Error(
          "Payment amount cannot be greater than the available invoice balance"
        );
      }

      const payload = {
        invoiceId: form.invoiceId ? Number(form.invoiceId) : null,
        vendorId: form.vendorId ? Number(form.vendorId) : null,
        paidTo: form.paidTo.trim() || selectedVendor?.name || undefined,
        paymentType: form.paymentType,
        amount,
        currency:
          selectedInvoice?.currency || form.currency || "GBP",
        paymentMethod: form.paymentMethod,
        status: form.status,
        paymentDate: form.paymentDate,
        dueDate: form.dueDate || null,
        notes: form.notes.trim() || null,
      };

      const res = payment
        ? await paymentApi.update(payment.id, payload)
        : await paymentApi.create(payload);

      onSaved?.(res.data?.message || "Payment saved successfully");
      onClose();
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to save payment"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const inputClass =
    "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 disabled:bg-slate-100 disabled:text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500";
  const labelClass =
    "mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300";

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40 px-4 py-6 backdrop-blur-[2px]">
      <div className="max-h-[92vh] w-full max-w-5xl overflow-y-auto rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5 dark:border-slate-800">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
              {payment ? "Edit Logistics Payment" : "New Logistics Payment"}
            </h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Add freight, customs, port, delivery, insurance, or invoice-linked payments.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="grid h-10 w-10 place-items-center rounded-full border border-slate-200 text-slate-500 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            <FiX />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-6 p-6">
          {error ? (
            <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
              <FiAlertCircle className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className={labelClass}>Invoice Optional</label>
              <select
                value={form.invoiceId}
                onChange={handleInvoiceChange}
                className={inputClass}
                disabled={saving}
              >
                <option value="">Direct payment without invoice</option>
                {invoices.map((inv) => (
                  <option key={inv.id} value={inv.id}>
                    {inv.invoiceNumber} — {inv.supplierName || inv.vendor?.name || "No forwarder"} — {inv.currency || "GBP"} {money(inv.balanceAmount).toFixed(2)} due
                  </option>
                ))}
              </select>
              <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                Changing the invoice safely reverses the old invoice balance
                and applies the payment to the newly selected invoice.
              </p>
            </div>

            <div>
              <label className={labelClass}>Forwarder / Payee</label>
              <select
                value={form.vendorId}
                onChange={(e) => updateForm("vendorId", e.target.value)}
                className={inputClass}
                disabled={saving}
              >
                <option value="">Select forwarder</option>
                {vendors.map((vendor) => (
                  <option key={vendor.id} value={vendor.id}>
                    {vendor.name || `Forwarder ${vendor.id}`}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Paid To Name</label>
              <input
                value={form.paidTo}
                onChange={(e) => updateForm("paidTo", e.target.value)}
                className={inputClass}
                placeholder="e.g. COSCO agent, UK customs, delivery company"
                disabled={saving}
              />
            </div>

            <div>
              <label className={labelClass}>Payment Type</label>
              <select
                value={form.paymentType}
                onChange={(e) => updateForm("paymentType", e.target.value)}
                className={inputClass}
                disabled={saving}
              >
                {paymentTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Amount *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.amount}
                onChange={(e) => updateForm("amount", e.target.value)}
                className={inputClass}
                placeholder={String(invoiceBalance || 0)}
                disabled={saving}
              />
            </div>

            <div>
              <label className={labelClass}>Currency</label>
              <select
                value={form.currency}
                onChange={(e) => updateForm("currency", e.target.value)}
                className={inputClass}
                disabled={saving}
              >
                {currencyOptions.map((cur) => (
                  <option key={cur} value={cur}>
                    {cur}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Payment Method</label>
              <select
                value={form.paymentMethod}
                onChange={(e) => updateForm("paymentMethod", e.target.value)}
                className={inputClass}
                disabled={saving}
              >
                {paymentMethods.map((method) => (
                  <option key={method.value} value={method.value}>
                    {method.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Status</label>
              <select
                value={form.status}
                onChange={(e) => updateForm("status", e.target.value)}
                className={inputClass}
                disabled={saving}
              >
                {paymentStatuses.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Payment Date</label>
              <input
                type="date"
                value={form.paymentDate}
                onChange={(e) => updateForm("paymentDate", e.target.value)}
                className={inputClass}
                disabled={saving}
              />
            </div>

            <div>
              <label className={labelClass}>Due Date Optional</label>
              <input
                type="date"
                value={form.dueDate}
                onChange={(e) => updateForm("dueDate", e.target.value)}
                className={inputClass}
                disabled={saving}
              />
            </div>

            <div className="md:col-span-2">
              <label className={labelClass}>Notes</label>
              <textarea
                rows="3"
                value={form.notes}
                onChange={(e) => updateForm("notes", e.target.value)}
                className={inputClass}
                placeholder="Optional notes"
                disabled={saving}
              />
            </div>

            {selectedInvoice ? (
              <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                <div className="grid gap-3 md:grid-cols-3">
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Invoice Total</p>
                    <p className="text-base font-semibold text-slate-800 dark:text-slate-100">
                      {displayCurrency} {invoiceTotal.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Paid So Far</p>
                    <p className="text-base font-semibold text-slate-800 dark:text-slate-100">
                      {displayCurrency} {invoicePaid.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Available for This Payment
                    </p>
                    <p className="text-base font-semibold text-slate-800 dark:text-slate-100">
                      {displayCurrency} {availableInvoiceBalance.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-slate-100 pt-4 dark:border-slate-800">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-2xl bg-blue-600 px-6 py-3 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {saving ? "Saving..." : payment ? "Update Payment" : "Create Payment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
